import { CondensedButton, IconModuleLine, Text, View } from "@instructure/ui";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ExternalService } from "../../../../../../services/ExternalService";
import { Card } from "../../../../../Card";
import RichTextEditor, {
  rteHasContent,
} from "../../../../../RichTextArea/RichTextEditor";
import RichTextViewer from "../../../../../RichTextArea/RichTextViewer";
import QualitativeMeasureEvidenceModal from "./QualitativeMeasureEvidenceModal";

/**
 * StructuredQuestionItem — Renders a single structured question with its
 * response editor (or read-only view), file controls, and evidence management.
 *
 * Props:
 * @param {Object}   question         — { id, question_text, helper_text, file_upload_enabled, sequence }
 * @param {number}   index            — 0-based index used for labeling (e.g. "Question 1")
 * @param {string}   measureId        — Parent measure's ddl_id, used for file paths
 * @param {string}   measureAssignees — Comma-separated assignee names/emails for the measure
 * @param {string}   userEmail        — Email of the current external user (passed to evidence modal)
 * @param {string}   responseText     — Current HTML response content to display/edit
 * @param {string}   responseId       — ID of the selected version response (used as RTE key for re-render)
 * @param {Function} onResponseChange — Callback (questionId, value) fired when the editor content changes
 * @param {boolean}  isAssigned       — Whether the current user is assigned to this measure (controls edit vs read-only)
 * @param {boolean}  isPreview        — Whether in preview/reporting mode (hides file controls)
 * @param {boolean}  isLast           — Whether this is the last question in the list (controls bottom border)
 */
const StructuredQuestionItem = ({
  question,
  index,
  measureId,
  measureAssignees,
  userEmail,
  responseText,
  responseId,
  onResponseChange,
  isAssigned,
  isPreview,
  isLast,
}) => {
  const { t } = useTranslation();

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
      {/* Question text (read-only) */}
      <Text weight="bold" size="medium">
        {question.question_text}
      </Text>

      {/* Helper text (read-only RCE content) */}
      {rteHasContent(question.helper_text) && (
        <RichTextViewer
          value={question.helper_text}
          ariaLabel={t("structuredQuestions.helperTextFor", {
            number: index + 1,
          })}
        />
      )}
      <View
        as="div"
        borderWidth={
          (isPreview || !isAssigned) && isLast ? undefined : "0 0 small 0"
        }
        padding={
          (isPreview || !isAssigned) && isLast ? undefined : "0 0 small 0"
        }
      >
        {isAssigned ? (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}
          >
            <RichTextEditor
              key={`${question.id}-${responseId}`}
              id={`structured-response-${question.id}`}
              label={<Text weight="bold">{t("results.measureResults")}</Text>}
              defaultValue={responseText}
              onChange={(value) => onResponseChange(question.id, value)}
            />
            {!isPreview && question.file_upload_enabled && (
              <ManageEvidenceButton
                measureId={measureId}
                question={question}
                userEmail=""
                isAssigned={isAssigned}
              />
            )}
          </div>
        ) : (
          <Card style={{ border: "1px solid #ccc", backgroundColor: "#fff" }}>
            {rteHasContent(responseText) ? (
              <RichTextViewer
                value={responseText}
                ariaLabel={t("results.measureResults")}
              />
            ) : (
              <Text fontStyle="italic">{t("results.noResponsePublished")}</Text>
            )}
            {!isPreview && question.file_upload_enabled && (
              <View
                as="div"
                borderWidth="small 0 0 0"
                margin="small 0 0 0"
                padding="small 0 0 0"
              >
                <ManageEvidenceButton
                  measureId={measureId}
                  question={question}
                  userEmail={userEmail}
                  measureAssignees={measureAssignees}
                  isAssigned={isAssigned}
                />
              </View>
            )}
          </Card>
        )}
      </View>
    </div>
  );
};

export default StructuredQuestionItem;

const ManageEvidenceButton = ({
  measureId,
  question,
  userEmail,
  measureAssignees,
  isAssigned,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [isPublished, setIsPublished] = useState();
  const [evidenceFiles, setEvidenceFiles] = useState(null);

  useEffect(() => {
    const fetchMeasureEvidenceFiles = async () => {
      try {
        const paths = await ExternalService.reviewFiles(
          "qualitativeEvidence",
          `${measureId}/${question.id}`
        );
        const published = paths.some(
          (file) => file.file_name === "response.html"
        );
        setIsPublished(published);
        setEvidenceFiles(
          paths.filter(
            (file) =>
              file.file_name !== "response.html" &&
              !file.file_path.includes("/draft/")
          )
        );
      } catch {
        setEvidenceFiles([]);
      }
    };

    fetchMeasureEvidenceFiles();
  }, [measureId, question.id]);

  const buttonLabel =
    evidenceFiles?.length > 0
      ? `${t("evidence.manageEvidence")} (${evidenceFiles.length})`
      : t("evidence.manageEvidence");

  return (
    <>
      <View as="span" display="inline-block">
        <CondensedButton
          renderIcon={IconModuleLine}
          onClick={() => setOpen(true)}
        >
          {buttonLabel}
        </CondensedButton>
      </View>
      <QualitativeMeasureEvidenceModal
        open={open}
        setOpen={setOpen}
        measureId={measureId}
        measureName={question.question_text}
        measureAssignees={measureAssignees}
        userEmail={userEmail}
        question={question}
        isPublished={isPublished}
        isAssigned={isAssigned}
        evidenceFiles={evidenceFiles}
        setEvidenceFiles={setEvidenceFiles}
      />
    </>
  );
};
